// ─────────────────────────────────────────────────────────────────────────────
// FTUE models
// ─────────────────────────────────────────────────────────────────────────────
// Types shared by the tip registry, the state store and the presenter.
// Deliberately free of Angular and of any feature import: features declare
// anchors and request tips, they never own the framework's vocabulary.
// ─────────────────────────────────────────────────────────────────────────────

import { FtueAnchorKey } from './ftue-anchors';

/**
 * Drives the label chip only — the card itself stays white. `alert` marks a tip
 * that needs a decision, `info` a hint, `new` a feature announcement.
 */
export type FtueTone = 'info' | 'alert' | 'new';

/**
 * Where the card sits relative to its anchor. `center` is the viewport-centred
 * fallback, used when a previewed tip's anchor does not exist on this page.
 */
export type FtuePlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

/** Declarative definition of one tip. Adding a tip is a change to this data. */
export interface IFtueTip {
  /**
   * Stable id. Also the i18n sub-namespace (`ftue.tips.<id>.*`) and the key
   * under which "seen" is persisted, so renaming one re-fires the tip.
   */
  id: string;

  /**
   * Bumped when the copy or the point of the tip changes. A user who saw an
   * older version becomes eligible again.
   */
  version: number;

  tone: FtueTone;

  /**
   * Highest wins when several tips become eligible in the same page view. The
   * losers are not marked seen, so they stay eligible for a later view.
   */
  priority: number;

  /**
   * Semantic anchor, never a CSS selector. `null` means the tip has no anchor
   * and always renders centred.
   */
  anchor: FtueAnchorKey | null;

  /**
   * Date (`YYYY-MM-DD`) after which the tip stops firing on its own. Required
   * for a feature announcement and forbidden elsewhere: "New" is a claim with a
   * shelf life, and nobody joining next year should be told a shipped feature is
   * new. An expired tip is still previewable from Settings — it is retired, not
   * deleted, and the entry documents what the product once announced.
   */
  expiresOn?: string;

  /**
   * Id of a tip that must have been seen before this one may fire on its own.
   * A prerequisite orders a pair of lessons that would otherwise compete for
   * the same page view — teach "these are alternatives, compare them" before
   * "this one matched nothing at all".
   *
   * Satisfied by having seen the prerequisite at ANY version, not the current
   * one: a copy edit to the earlier tip should not silently re-lock the later
   * one for everybody who already learned the concept.
   *
   * Consequence worth knowing: a tip whose prerequisite the user never
   * encounters never fires on its own. It stays previewable from Settings.
   * `Preview` ignores prerequisites, as it ignores every other rule.
   */
  requires?: string;

  placement: FtuePlacement;
}

/** Per-user tip state. One record per user id, see `FtueStateStore`. */
export interface IFtueUserState {
  /** Master switch from Settings → Product tours & tips. */
  tipsEnabled: boolean;

  /** Tip id → the `version` of the tip that was seen. */
  seen: Record<string, number>;
}

/** Everything on by default: a new user should meet the tips. */
export const FTUE_DEFAULT_STATE: IFtueUserState = {
  tipsEnabled: true,
  seen: {},
};

/**
 * How a shown tip ended. All four mark it seen — including `outside`, because
 * clicking away is a deliberate "not now, and I've read the room".
 */
export type FtueDismissReason = 'action' | 'close' | 'outside' | 'escape';

/**
 * A tip the presenter has decided to show. Resolving the anchor element is the
 * renderer's job, not the presenter's — that keeps every eligibility rule
 * testable without a DOM.
 */
export interface IFtuePresentation {
  tip: IFtueTip;
  /** Previews never consume budget and never mark the tip seen. */
  isPreview: boolean;
}
